import { AuthApi, ResponseError } from '@linktivity/ars-neo';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@link/share/constants';
import { createArsConfig, type ArsCredentials } from '../services/ars';

/**
 * The visitor's ARS session: an access and a refresh token in localStorage,
 * under the same keys as the SPA so a sign-in there is a sign-in here.
 *
 * Safe to import anywhere. Storage is read when a function is called, never at
 * import, and a server render has none: every visitor reads as logged out
 * there, which keeps the HTML the same for all of them.
 */

function storage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    // Storage disabled (Safari private mode, blocked site data).
    return undefined;
  }
}

export function getAccessToken(): string | undefined {
  return storage()?.getItem(ACCESS_TOKEN) || undefined;
}

function getRefreshToken(): string | undefined {
  return storage()?.getItem(REFRESH_TOKEN) || undefined;
}

export function setTokens(tokens: {
  accessToken?: string;
  refreshToken?: string;
}) {
  const store = storage();
  if (tokens.accessToken) store?.setItem(ACCESS_TOKEN, tokens.accessToken);
  if (tokens.refreshToken) store?.setItem(REFRESH_TOKEN, tokens.refreshToken);
}

export function clearTokens() {
  const store = storage();
  store?.removeItem(ACCESS_TOKEN);
  store?.removeItem(REFRESH_TOKEN);
}

// Refreshing needs no token of its own, and an anonymous client keeps a
// failed refresh from triggering another refresh.
const anonymousAuthApi = new AuthApi(createArsConfig());

let refreshing: Promise<string | undefined> | undefined;

// Refresh responses meaning the refresh token itself is no good. Anything else
// (429, 5xx, offline) says nothing about the session, so it is kept for a
// later attempt rather than signing the visitor out over an outage.
const REFRESH_TOKEN_REJECTED = new Set([400, 401, 403]);

/**
 * A new access token from the refresh token. Concurrent callers share one
 * request: several API calls failing at once must not each spend the refresh
 * token. A refresh the API rejects ends the session.
 *
 * The outcome only applies to the session it was asked for: if the stored
 * refresh token changed meanwhile (logged out, or signed in again), the
 * result is dropped, so a refresh in flight cannot restore a session the
 * visitor has just ended.
 */
export function refreshAccessToken(): Promise<string | undefined> {
  refreshing ??= (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      return undefined;
    }
    const sameSession = () => getRefreshToken() === refreshToken;
    try {
      const res = await anonymousAuthApi.refreshToken({
        body: { refreshToken }
      });
      if (!sameSession()) return undefined;
      setTokens(res);
      return res.accessToken;
    } catch (error) {
      if (
        error instanceof ResponseError &&
        REFRESH_TOKEN_REJECTED.has(error.response.status) &&
        sameSession()
      ) {
        clearTokens();
      }
      return undefined;
    }
  })().finally(() => {
    refreshing = undefined;
  });
  return refreshing;
}

/** Credentials for a client acting as the visitor. */
export const visitorCredentials: ArsCredentials = {
  getAccessToken,
  refreshAccessToken,
  onRejected: clearTokens
};

const REVOKE_TIMEOUT_MS = 3000;

/**
 * Revoke the tokens server-side and forget them. Time-boxed and best-effort:
 * a stalled or failed revoke never keeps the visitor signed in locally.
 */
export async function revokeTokens() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  clearTokens();
  if (!accessToken && !refreshToken) return;
  try {
    await new AuthApi(
      createArsConfig({
        getAccessToken: () => accessToken,
        // Nothing to renew or end: the session is already over locally.
        refreshAccessToken: async () => undefined,
        onRejected: () => {}
      })
    ).revokeToken(
      { body: { accessToken, refreshToken } },
      { signal: AbortSignal.timeout(REVOKE_TIMEOUT_MS) }
    );
  } catch {
    // Ignore: the local session is already cleared.
  }
}
